import { createContext, useContext, useEffect, useState } from 'react';
import type { ReactNode } from 'react';
import type { Theme } from '@annoteer/platform-adapter';
import { usePlatform } from './PlatformContext.js';

interface ThemeContextValue {
  theme: Theme;
}

const ThemeContext = createContext<ThemeContextValue>({ theme: 'default' });

export function ThemeProvider({ children }: { children: ReactNode }) {
  const { adapter, context } = usePlatform();
  const [theme, setTheme] = useState<Theme>(context?.theme ?? 'default');

  useEffect(() => {
    adapter.onThemeChange(setTheme);
  }, [adapter]);

  // Apply theme as data attribute for CSS targeting
  useEffect(() => {
    const themeMap: Record<Theme, string> = {
      default: 'light',
      dark: 'dark',
      contrast: 'high-contrast',
    };
    document.documentElement.setAttribute('data-theme', themeMap[theme]);
  }, [theme]);

  return <ThemeContext.Provider value={{ theme }}>{children}</ThemeContext.Provider>;
}

export function useTheme(): ThemeContextValue {
  return useContext(ThemeContext);
}
